import i18next from 'i18next';
import { storeConstants } from '@utils/constants';
import {
    normalizeOrdersList,
    normalizeOrdersListOneOrder,
    normalizeReportOrderEventTable,
    normalizeOrderEventTableTotals,
    normalizeReportOrderHotelReservationTable,
} from '@utils/storeDataNormalization';
import { mutationTypes } from '@helpers/storeFactories/helpers';
import storeModuleFactory from '@helpers/storeModuleFactory';
import { apiService } from '@api';
import ordersDetailSection from './ordersDetailSection';
import notificationUtils from '@/utils/notificationUtils';

const STORE_NAME = 'orders';

const store = storeModuleFactory.createModuleView(
    STORE_NAME,
    {
        get: apiService.orders.getOrdersByCustomerId,
        one: apiService.orders.one,
    },
    {
        actionGetOptionsDefault: {
            refresh: false,
            offset: 0,
            limit: storeConstants.ITEMS_PER_PAGE,
        },
        mapListItemFn: normalizeOrdersList,
    },
    {
        state: {
            filters: {},
        },
        getters: {
            filters: (state) => state.filters,
            total: (state) => state.totalCount,
            one: (state) => state.detail,
            oneBillingDetails: (state) => state.detail.billingDetails,
            oneOrder: (state) => (state.detail ? normalizeOrdersListOneOrder(state.detail) : null),
            onePaymentDetails: (state) => state.detail.paymentInfo,
            oneOrderEventTable: (state) => normalizeReportOrderEventTable(state),
            oneOrderEventTableTotal: (state) => normalizeOrderEventTableTotals(state),
            oneOrderHotelReservationTable: (state) =>
                normalizeReportOrderHotelReservationTable(state),
        },
        mutations: {
            [mutationTypes.UPDATE_FILTERS](state, { filters }) {
                state.filters = filters;
            },
            [mutationTypes.CLEAR_DETAILS_DATA](state) {
                state.detail = {};
            },
        },
        actions: {
            async resendConfirmationEmail({ dispatch }, { orderId, hotelRoomInfo }) {
                let response = await apiService.orders.resendConfirmationEmail({
                    orderId,
                    hotelRoomInfo,
                });
                if (response.success) {
                    dispatch(
                        'notification/success',
                        i18next.t(
                            'main:views.customerSearch.theCustomersSearch._components.resendConfirmationEmailModal.successAlertMessage',
                            { orderId }
                        ),
                        { root: true }
                    );
                } else {
                    dispatch('notification/error', notificationUtils.getFromApiResponse(response), {
                        root: true,
                    });
                }
                return !!response.success;
            },
             async resendCancellationEmail({ dispatch }, { orderId, hotelRoomInfo }) {
                const response = await apiService.orders.resendCancellationEmail({
                    orderId,
                    hotelRoomInfo,
                });
                if (response.success) {
                    dispatch(
                        'notification/success',
                        i18next.t(
                            'main:views.customerSearch.theCustomersSearch._components.resendConfirmationEmailModal.cancellation.successAlertMessage',
                            { orderId }
                        ),
                        { root: true }
                    );
                } else {
                    dispatch('notification/error', notificationUtils.getFromApiResponse(response), {
                        root: true,
                    });
                }
                return !!response.success;
            },
            async resendActionRequiredEmail({ dispatch }, { orderId, orderUuid }) {
                let response = await apiService.orders.resendActionRequiredEmail({
                    orderUuid,
                });
                if (response.success) {
                    dispatch(
                        'notification/success',
                        i18next.t(
                            'main:views.customerSearch.theCustomersSearch._components.resendActionRequiredModal.successAlertMessage',
                            { orderId }
                        ),
                        { root: true }
                    );
                } else {
                    dispatch('notification/error', notificationUtils.getFromApiResponse(response), {
                        root: true,
                    });
                }
                return !!response.success;
            },
            async cancelBooking(context, { orderId, orderPartnerId }) {
                let response = await apiService.orders.cancelBooking({ orderId, orderPartnerId });
                return response;
                /* Commented for GAP-916, keeping it because it might be needed for GAP-919
                if ( response.success ) {
                    dispatch( 'notification/success',
                        {
                            title: i18next.t( 'main:views.customerSearch.theCustomersSearch._components.cancelBookingModal.successAlertMessage.title' ),
                            content: i18next.t( 'main:views.customerSearch.theCustomersSearch._components.cancelBookingModal.successAlertMessage.subtitle', { orderId } ),

                        },
                        { root: true }
                    );
                } else {
                    dispatch( 'notification/error', notificationUtils.getFromApiResponse( response ), { root: true } );
                }
                return !!response.success; */
            },
            async refundBooking(context, { orderUuid }) {
                let response = await apiService.orders.refundBooking({ orderUuid });
                return response;
            },
            async updateFilters({ commit }, { filters }) {
                return commit(mutationTypes.UPDATE_FILTERS, { filters });
            },
            clearDetailsData({ commit }) {
                return commit(mutationTypes.CLEAR_DETAILS_DATA);
            },
            async getHotelSupplierBookingStatus(context, { orderId, orderPartnerId }) {
                return await apiService.orders.getHotelSupplierBookingStatus(
                    orderId,
                    orderPartnerId
                );
            },
            async getTrueCancellationPolicies(context, { orderId }) {
                return await apiService.orders.getTrueCancellationPolicies(orderId);
            },
        },
        modules: {
            detailSection: ordersDetailSection,
        },
    }
);

export default store;
